/**
 * Containers tab: the pure logic behind the rebuild of the client's own
 * Container Inventory tool (wave 1).
 *
 * Everything that decides what a number means lives here, so node --test can
 * drive it with synthetic rows. The data access is containerInventoryData.ts
 * and the rendering is components/ContainerInventoryWidget.tsx; neither makes
 * a decision this file does not.
 *
 * THE CLIENT'S FORMULA, NOT OURS. The tool counts completed work orders by
 * scheduled month, division and container size:
 *   roll off   completed work orders of the customer's delivery and removal
 *              work types, on a route that is not the client's delete route;
 *   front load completed work orders of the customer's delivery, removal and
 *              other container work types (extra pickup, exchange, relocate).
 * The work type literals are the CUSTOMER's (config.yaml container_inventory,
 * injected by the resolver), never a literal in this file (MER-819).
 *
 * THEIR SIGN (AJ ruling, 24 Sep): net is removals minus deliveries and is
 * called Net Yard, the way the client's tool labels it: containers coming back
 * to the yard. Positive means the yard is filling. The one place their tool
 * uses the other sign, it says so in the name (Net FL Placed, deliveries minus
 * removals, on the board memo), and that is kept verbatim.
 *
 * SUM OVER SUM (MER-715). Every share and rate here divides one total by
 * another total. Nothing averages a per-month or per-division ratio.
 *
 * NUMBER ONLY WHEN DATA BACKS IT. A ratio with a zero denominator is null, and
 * the renderer prints a dash. A month with no rows at all is reported as
 * having no rows, not as a month of zeros.
 */

/**
 * The lines of business this tab covers, in display order. Hook lift and
 * tractor trailer are their own lines, each counted on its own work types and
 * never pooled with roll off (AJ 2026-10-21, #1057); a customer gets a line
 * only when its config names that line's work types.
 */
export const CONTAINER_LOBS = ["roll_off", "front_load", "hook_lift", "tractor_trailer"];

/** Short labels the client's tool uses (HL and TT follow the same pattern). */
export const LOB_SHORT = {
  roll_off: "RO",
  front_load: "FL",
  hook_lift: "HL",
  tractor_trailer: "TT",
};

/** Roles a configured work type can play. */
export const ROLES = ["delivery", "removal", "other"];

/** The fallback size label when a work order carries no container volume. */
export const OTHER_SIZE = "Other";

function asList(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => typeof v === "string" && v.trim()).map((v) => v.trim());
}

/**
 * Normalise the resolver-injected config into {roll_off?, front_load?}, each
 * {delivery, removal, other, exclude_route_prefixes}. A line with neither a
 * delivery nor a removal list is dropped: it cannot produce a single number on
 * this tab. Returns null when nothing usable is left.
 */
export function normalizeContainerConfig(raw) {
  if (!raw || typeof raw !== "object") return null;
  const out = {};
  for (const lob of CONTAINER_LOBS) {
    const cfg = raw[lob];
    if (!cfg || typeof cfg !== "object") continue;
    const delivery = asList(cfg.delivery);
    const removal = asList(cfg.removal);
    if (delivery.length === 0 && removal.length === 0) continue;
    out[lob] = {
      delivery,
      removal,
      other: asList(cfg.other),
      exclude_route_prefixes: asList(cfg.exclude_route_prefixes),
    };
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Every work type literal a line's query needs, in a stable order. */
export function workTypesFor(lobCfg) {
  if (!lobCfg) return [];
  return [...new Set([...lobCfg.delivery, ...lobCfg.removal, ...lobCfg.other])];
}

/** delivery | removal | other | null for one work type on one line. */
export function roleFor(workType, lobCfg) {
  if (!lobCfg || typeof workType !== "string") return null;
  const wt = workType.trim();
  if (lobCfg.delivery.includes(wt)) return "delivery";
  if (lobCfg.removal.includes(wt)) return "removal";
  if (lobCfg.other.includes(wt)) return "other";
  return null;
}

/**
 * True when a route id is the client's delete route. The pipeline suffixes a
 * route name that spans lines of business ("_roll_off") or divisions
 * (" (Jackson)") to keep it unique, so the client's exact-name rule becomes
 * "the name itself, or the name followed by one of those suffixes".
 * Case-sensitive, as the client's tool is.
 */
export function isExcludedRoute(routeId, prefixes) {
  if (typeof routeId !== "string" || !prefixes || prefixes.length === 0) return false;
  for (const p of prefixes) {
    if (routeId === p || routeId.startsWith(`${p}_`) || routeId.startsWith(`${p} (`)) return true;
  }
  return false;
}

/** "30 YD" from a container volume in cubic yards; OTHER_SIZE when unknown. */
export function sizeLabel(volume) {
  const n = Number(volume);
  if (volume === null || volume === undefined || volume === "" || !Number.isFinite(n) || n <= 0) {
    return OTHER_SIZE;
  }
  const rounded = Math.round(n * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} YD`;
}

/** Numeric sort key for a size label, OTHER_SIZE last. */
export function sizeSortKey(label) {
  const m = /^([0-9.]+) YD$/.exec(label ?? "");
  return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
}

/** Sizes ordered largest first (the client lists 40/30/20 and 8/6/4/2), Other last. */
export function sortSizes(sizes) {
  return [...new Set(sizes)].sort((a, b) => {
    const ka = sizeSortKey(a);
    const kb = sizeSortKey(b);
    if (ka === kb) return String(a).localeCompare(String(b));
    if (!Number.isFinite(ka)) return 1;
    if (!Number.isFinite(kb)) return -1;
    return kb - ka;
  });
}

/** YYYY-MM from a YYYY-MM-DD event date; null when unparseable. */
export function monthKey(date) {
  if (typeof date !== "string") return null;
  const m = /^(\d{4})-(\d{2})/.exec(date.trim());
  return m ? `${m[1]}-${m[2]}` : null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Aug 2026" from "2026-08". */
export function monthLabel(mk) {
  const m = /^(\d{4})-(\d{2})$/.exec(mk ?? "");
  if (!m) return String(mk ?? "");
  return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** The month before a YYYY-MM key. */
export function previousMonth(mk) {
  const m = /^(\d{4})-(\d{2})$/.exec(mk ?? "");
  if (!m) return null;
  let y = Number(m[1]);
  let mo = Number(m[2]) - 1;
  if (mo === 0) {
    mo = 12;
    y -= 1;
  }
  return `${y}-${String(mo).padStart(2, "0")}`;
}

/** The last complete calendar month before `today` (a Date). */
export function lastCompleteMonth(today) {
  return previousMonth(currentMonth(today));
}

/** Division slug for comparison with the page chip ("Jackson" -> "jackson"). */
export function divisionSlug(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

/** Display name for a division value ("central_coast" -> "Central Coast"). */
export function divisionLabel(value) {
  const s = String(value ?? "").trim();
  if (!s) return "Unassigned";
  if (/[A-Z]/.test(s)) return s;
  return s
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Aggregate rows (one per event_date x division x work_type x volume, with a
 * count) into records the views read. Rows whose work type plays no role on
 * the line are dropped; so are rows with no parseable date, because a count
 * that cannot be placed in a month cannot honestly be shown in one.
 *
 * @param {Array<{lob:string, event_date:string, division:string|null,
 *   work_type:string, volume_collected_cy:number|null, count:number|string,
 *   route_id?:string|null}>} rows
 * @param {object} cfg normalizeContainerConfig output
 */
export function buildRecords(rows, cfg) {
  const out = [];
  if (!cfg || !Array.isArray(rows)) return out;
  for (const r of rows) {
    const lobCfg = cfg[r.lob];
    if (!lobCfg) continue;
    const role = roleFor(r.work_type, lobCfg);
    if (!role) continue;
    if (isExcludedRoute(r.route_id, lobCfg.exclude_route_prefixes)) continue;
    const mk = monthKey(r.event_date);
    if (!mk) continue;
    const n = Number(r.count ?? 1);
    if (!Number.isFinite(n) || n <= 0) continue;
    out.push({
      lob: r.lob,
      role,
      work_type: String(r.work_type).trim(),
      date: String(r.event_date).slice(0, 10),
      mk,
      div: divisionLabel(r.division),
      divSlug: divisionSlug(r.division),
      size: sizeLabel(r.volume_collected_cy),
      n,
    });
  }
  return out;
}

/** Keep records in the chosen divisions (page chip slugs); all when empty. */
export function filterDivisions(records, divisionSlugs) {
  const wanted = new Set((divisionSlugs ?? []).map(divisionSlug).filter(Boolean));
  if (wanted.size === 0) return records;
  return records.filter((r) => wanted.has(r.divSlug));
}

function sum(records, pred) {
  let total = 0;
  for (const r of records) if (pred(r)) total += r.n;
  return total;
}

/** Sorted distinct month keys with at least one record. */
export function monthsOf(records) {
  return [...new Set(records.map((r) => r.mk))].sort();
}

/** Sorted distinct division labels. */
export function divisionsOf(records) {
  return [...new Set(records.map((r) => r.div))].sort();
}

/** removals minus deliveries: the client's Net Yard. */
export function netYard(deliveries, removals) {
  return removals - deliveries;
}

/**
 * Percent change, rounded to one decimal (-17.3, not -17), which the tile
 * prints as "-17.3%". Rounding to a whole percent here made the tile print
 * "-17.0%", a false decimal. Null when the prior value is zero (no base to
 * compare against).
 */
export function pctChange(current, prior) {
  if (!Number.isFinite(prior) || prior === 0 || !Number.isFinite(current)) return null;
  return Math.round(((current - prior) / prior) * 1000) / 10;
}

/** Share of a total, as a fraction. Null when the total is zero. */
export function share(part, total) {
  if (!Number.isFinite(total) || total === 0) return null;
  return part / total;
}

/**
 * The client's "RO Utilization Rate": deliveries as a percentage of removals,
 * one decimal. Null when there are no removals (their tool prints n/a when
 * deliveries exist and 0 when both are zero; a zero-over-zero is not a rate,
 * so both read as no number here).
 */
export function utilizationRate(deliveries, removals) {
  if (!Number.isFinite(removals) || removals === 0) return null;
  return Math.round((deliveries / removals) * 1000) / 10;
}

/** Counts for one month: the headline tiles. */
export function monthCounts(records, mk) {
  const inMonth = records.filter((r) => r.mk === mk);
  const c = (lob, role) => sum(inMonth, (r) => r.lob === lob && r.role === role);
  const roDel = c("roll_off", "delivery");
  const roRem = c("roll_off", "removal");
  const flDel = c("front_load", "delivery");
  const flRem = c("front_load", "removal");
  return {
    month: mk,
    rows: inMonth.length,
    ro_delivery: roDel,
    ro_removal: roRem,
    ro_net_yard: netYard(roDel, roRem),
    fl_delivery: flDel,
    fl_removal: flRem,
    fl_net_yard: netYard(flDel, flRem),
    total_work_orders: sum(inMonth, () => true),
  };
}

/** Metric ids the KPI view can show, with the monthCounts key each reads. */
export const KPI_METRICS = {
  ro_delivery: { key: "ro_delivery", lob: "roll_off" },
  ro_removal: { key: "ro_removal", lob: "roll_off" },
  ro_net_yard: { key: "ro_net_yard", lob: "roll_off", signed: true },
  fl_delivery: { key: "fl_delivery", lob: "front_load" },
  fl_removal: { key: "fl_removal", lob: "front_load" },
  fl_net_yard: { key: "fl_net_yard", lob: "front_load", signed: true },
  total_work_orders: { key: "total_work_orders", lob: null },
};

/**
 * One headline tile: the month's value, the prior month's, and the change.
 * `value` is null when the month has no rows at all (awaiting data), never 0.
 */
export function kpiTile(records, metric, mk) {
  const spec = KPI_METRICS[metric];
  if (!spec) return null;
  const cur = monthCounts(records, mk);
  const prevMk = previousMonth(mk);
  const prev = monthCounts(records, prevMk);
  const value = cur.rows > 0 ? cur[spec.key] : null;
  const prior = prev.rows > 0 ? prev[spec.key] : null;
  return {
    metric,
    month: mk,
    value,
    prior,
    prior_month: prevMk,
    // A signed net has no meaningful percent change (a swing through zero).
    change_pct: !spec.signed && value !== null && prior !== null ? pctChange(value, prior) : null,
    change_abs: spec.signed && value !== null && prior !== null ? value - prior : null,
  };
}

/**
 * Inventory snapshot for one line in one month: per division, one row per
 * container size with deliveries, removals and Net Yard, plus the division
 * total. Every size present in the data is shown; the total is the sum of the
 * rows shown, so the card adds up on its face.
 */
export function inventorySnapshot(records, lob, mk) {
  const scoped = records.filter(
    (r) => r.lob === lob && r.mk === mk && (r.role === "delivery" || r.role === "removal"),
  );
  return divisionsOf(scoped).map((div) => {
    const inDiv = scoped.filter((r) => r.div === div);
    const rows = sortSizes(inDiv.map((r) => r.size)).map((size) => {
      const d = sum(inDiv, (r) => r.size === size && r.role === "delivery");
      const rm = sum(inDiv, (r) => r.size === size && r.role === "removal");
      return { size, deliveries: d, removals: rm, net_yard: netYard(d, rm) };
    });
    const d = rows.reduce((a, r) => a + r.deliveries, 0);
    const rm = rows.reduce((a, r) => a + r.removals, 0);
    return {
      division: div,
      rows,
      total: { deliveries: d, removals: rm, net_yard: netYard(d, rm) },
    };
  });
}

/**
 * Monthly summary: one row per month with RO and FL deliveries and removals,
 * their total, and Net RO Yard, plus a totals row. The client's "All" view.
 */
export function monthlySummary(records) {
  const rows = monthsOf(records).map((mk) => {
    const c = monthCounts(records, mk);
    return {
      month: mk,
      ro_delivery: c.ro_delivery,
      ro_removal: c.ro_removal,
      fl_delivery: c.fl_delivery,
      fl_removal: c.fl_removal,
      total: c.ro_delivery + c.ro_removal + c.fl_delivery + c.fl_removal,
      net_ro_yard: c.ro_net_yard,
      net_fl_yard: c.fl_net_yard,
    };
  });
  const t = (k) => rows.reduce((a, r) => a + r[k], 0);
  const totals = {
    ro_delivery: t("ro_delivery"),
    ro_removal: t("ro_removal"),
    fl_delivery: t("fl_delivery"),
    fl_removal: t("fl_removal"),
    total: t("total"),
  };
  totals.net_ro_yard = netYard(totals.ro_delivery, totals.ro_removal);
  totals.net_fl_yard = netYard(totals.fl_delivery, totals.fl_removal);
  return { rows, totals };
}

/**
 * Division activity: each division's share of each line's work orders over
 * every month loaded. RO counts deliveries and removals; FL counts every
 * configured front load container work type (the client's tool does the same).
 */
export function divisionActivity(records) {
  const roTot = sum(records, (r) => r.lob === "roll_off");
  const flTot = sum(records, (r) => r.lob === "front_load");
  return divisionsOf(records).map((div) => {
    const ro = sum(records, (r) => r.div === div && r.lob === "roll_off");
    const fl = sum(records, (r) => r.div === div && r.lob === "front_load");
    return {
      division: div,
      ro_work_orders: ro,
      ro_share: share(ro, roTot),
      fl_work_orders: fl,
      fl_share: share(fl, flTot),
    };
  });
}

/** Size by month counts for one line, with row and column totals. */
export function sizeHeatmap(records, lob) {
  const scoped = records.filter((r) => r.lob === lob);
  const months = monthsOf(scoped);
  const sizes = sortSizes(scoped.map((r) => r.size));
  let max = 0;
  const rows = sizes.map((size) => {
    const cells = months.map((mk) => {
      const v = sum(scoped, (r) => r.size === size && r.mk === mk);
      if (v > max) max = v;
      return v;
    });
    return { size, cells, total: cells.reduce((a, b) => a + b, 0) };
  });
  const colTotals = months.map((mk) => sum(scoped, (r) => r.mk === mk));
  return { months, rows, colTotals, total: colTotals.reduce((a, b) => a + b, 0), max };
}

/** Heat bucket 0..5 for a cell, the client's five-step shading. */
export function heatBucket(value, max) {
  if (!value || !max) return 0;
  const r = value / max;
  if (r < 0.2) return 1;
  if (r < 0.4) return 2;
  if (r < 0.6) return 3;
  if (r < 0.8) return 4;
  return 5;
}

/** The four series the daily rate and trend views read. */
export const ACTIVITY_SERIES = [
  { id: "ro_delivery", lob: "roll_off", role: "delivery", label: "RO Deliveries" },
  { id: "ro_removal", lob: "roll_off", role: "removal", label: "RO Removals" },
  { id: "fl_delivery", lob: "front_load", role: "delivery", label: "FL Deliveries" },
  { id: "fl_removal", lob: "front_load", role: "removal", label: "FL Removals" },
];

/**
 * Daily activity rate: work orders per active day, where an active day is a
 * scheduled date with any in-scope container work that month (the client's
 * definition; not a calendar or operating day). Per month and over every
 * month, each as one total over one total.
 */
export function dailyActivityRate(records, series = ACTIVITY_SERIES) {
  const months = monthsOf(records);
  const daysIn = (mk) => new Set(records.filter((r) => r.mk === mk).map((r) => r.date)).size;
  const allDays = new Set(records.map((r) => r.date)).size;
  const rows = series.map((s) => {
    const cells = months.map((mk) => {
      const days = daysIn(mk);
      const v = sum(records, (r) => r.mk === mk && r.lob === s.lob && r.role === s.role);
      return { count: v, days, rate: days > 0 && v > 0 ? v / days : null };
    });
    const totV = sum(records, (r) => r.lob === s.lob && r.role === s.role);
    return {
      id: s.id,
      label: s.label,
      cells,
      count: totV,
      days: allDays,
      rate: allDays > 0 ? totV / allDays : null,
    };
  });
  return { months, rows };
}

/** Share of a line's work orders by container size (sum over sum). */
export function sizeMix(records, lob) {
  const scoped = records.filter((r) => r.lob === lob);
  const total = sum(scoped, () => true);
  return sortSizes(scoped.map((r) => r.size)).map((size) => {
    const c = sum(scoped, (r) => r.size === size);
    return { size, count: c, share: share(c, total) };
  });
}

/** Monthly trend chart series (counts per month for the four series). */
export function activityTrend(records) {
  const months = monthsOf(records);
  return {
    months,
    series: ACTIVITY_SERIES.map((s) => ({
      id: s.id,
      label: s.label,
      lob: s.lob,
      values: months.map((mk) =>
        sum(records, (r) => r.mk === mk && r.lob === s.lob && r.role === s.role),
      ),
    })),
  };
}

function activityRow(records, mk, div) {
  const inScope = records.filter((r) => r.mk === mk && (div === null || r.div === div));
  const c = (lob, role) => sum(inScope, (r) => r.lob === lob && r.role === role);
  const rd = c("roll_off", "delivery");
  const rr = c("roll_off", "removal");
  const fd = c("front_load", "delivery");
  const fr = c("front_load", "removal");
  return {
    division: div ?? "Combined",
    ro_delivery: rd,
    ro_removal: rr,
    ro_utilization: utilizationRate(rd, rr),
    fl_delivery: fd,
    fl_removal: fr,
    net_fl_placed: fd - fr,
    total: rd + rr + fd + fr,
  };
}

/**
 * The monthly board memo: the chosen month's activity by division and
 * combined, the RO Utilization Rate by month with Net RO Yard, and Net FL
 * Placed by month. `summary` is the memo's opening sentence built from the
 * same numbers.
 */
export function memo(records, mk) {
  const divisions = divisionsOf(records.filter((r) => r.mk === mk))
    .map((div) => activityRow(records, mk, div))
    .filter((row) => row.total > 0);
  const combined = activityRow(records, mk, null);
  const months = monthsOf(records);
  const roTrend = months.map((m) => {
    const c = monthCounts(records, m);
    return {
      month: m,
      ro_delivery: c.ro_delivery,
      ro_removal: c.ro_removal,
      ro_utilization: utilizationRate(c.ro_delivery, c.ro_removal),
      net_ro_yard: c.ro_net_yard,
    };
  });
  const flTrend = months.map((m) => {
    const c = monthCounts(records, m);
    return {
      month: m,
      fl_delivery: c.fl_delivery,
      fl_removal: c.fl_removal,
      net_fl_placed: c.fl_delivery - c.fl_removal,
    };
  });
  const prevMk = previousMonth(mk);
  const prevRow = roTrend.find((r) => r.month === prevMk) ?? null;
  return {
    month: mk,
    divisions,
    combined,
    roTrend,
    flTrend,
    summary: memoSummary(mk, combined, prevRow),
  };
}

function signed(n) {
  return `${n >= 0 ? "+" : ""}${n}`;
}

/**
 * The memo's "prior month" phrase for RO utilization. The prior month is the
 * calendar month immediately before the selected one, never the second-to-last
 * month in the data. When that month has no data the phrase says so rather
 * than comparing against some other month. `hasPrior` is whether it has data.
 */
export function priorMonthClause(hasPrior, util, priorUtil) {
  if (!hasPrior) return " (no prior month data)";
  if (util === null || priorUtil === null) return "";
  const word = util > priorUtil ? "up from" : util < priorUtil ? "down from" : "flat against";
  return ` (${word} ${priorUtil}% the prior month)`;
}

/**
 * Every comparison in the memo, against the calendar month before `mk`.
 * `months` is the list of months that have data; `tally(month)` returns
 * { ro_delivery, ro_removal } for one month. The highlighted trend row is `mk`
 * itself, so callers compare `month === mk`, never "the last month".
 */
export function priorMonthComparison(mk, months, tally) {
  const priorMk = previousMonth(mk);
  const hasPrior = priorMk !== null && months.includes(priorMk);
  const cur = tally(mk);
  const util = utilizationRate(cur.ro_delivery, cur.ro_removal);
  let priorUtil = null;
  if (hasPrior) {
    const prev = tally(priorMk);
    priorUtil = utilizationRate(prev.ro_delivery, prev.ro_removal);
  }
  return {
    month: mk,
    priorMonth: priorMk,
    hasPrior,
    util,
    priorUtil,
    clause: priorMonthClause(hasPrior, util, priorUtil),
  };
}

/** The memo's opening sentence, from the numbers alone. */
export function memoSummary(mk, combined, prevRoRow) {
  const util = combined.ro_utilization;
  const utilText = util === null ? "not available (no removals)" : `${util}%`;
  const direction = priorMonthClause(
    prevRoRow !== null && prevRoRow !== undefined,
    util,
    prevRoRow ? prevRoRow.ro_utilization : null,
  );
  return (
    `${monthLabel(mk)}: RO Utilization Rate ${utilText}${direction}. ` +
    `Net FL Placed ${signed(combined.net_fl_placed)} ` +
    `(${combined.fl_delivery} FL deliveries, ${combined.fl_removal} FL removals).`
  );
}

/** Counts on one scheduled date, by line and role (the Daily Activity Tracker). */
export function dailyCounts(records, date) {
  const onDay = records.filter((r) => r.date === date);
  const c = (lob, role) => sum(onDay, (r) => r.lob === lob && r.role === role);
  return {
    date,
    ro_delivery: c("roll_off", "delivery"),
    ro_removal: c("roll_off", "removal"),
    fl_delivery: c("front_load", "delivery"),
    fl_removal: c("front_load", "removal"),
    other: sum(onDay, (r) => r.role === "other"),
    total: sum(onDay, () => true),
  };
}

/** The latest scheduled date with activity in a month, or null. */
export function latestActiveDate(records, mk) {
  const dates = records.filter((r) => r.mk === mk).map((r) => r.date);
  if (dates.length === 0) return null;
  return dates.sort()[dates.length - 1];
}

/** True when a YYYY-MM month has fully closed before `today`. */
export function isCompleteMonth(mk, today) {
  const last = lastCompleteMonth(today);
  return typeof mk === "string" && /^\d{4}-\d{2}$/.test(mk) && last !== null && mk <= last;
}

/**
 * The month every month-scoped view shows: the page's explicit month when one
 * is chosen AND it has closed, otherwise the last complete calendar month.
 * Never "the latest month with data": a lagging feed must read as a lagging
 * feed, and an in-progress month must never be compared with a full one.
 */
export function selectedMonth(explicitMonth, today) {
  if (isCompleteMonth(explicitMonth, today)) return explicitMonth;
  return lastCompleteMonth(today);
}

/**
 * Drop every record in a month that has not closed yet (MER-553 / MER-693).
 * A half-month file would otherwise read as a fall in every trend, table and
 * heatmap, and as a fake drop against the prior month. Every wave 1 widget
 * reads the records through this, with the default.
 *
 * `throughCurrentMonth: true` (AJ 2026-09-29, "Everything should show the
 * current month when possible") keeps the in-progress month too, and still
 * drops anything dated after it. The Containers report port reads this way,
 * as the client's own page does (it shows every month it holds and marks no
 * month as partial). The default is unchanged, so every closed-months reader
 * keeps its numbers; the cut-off lives here once for both.
 */
export function completeMonthsOnly(records, today, opts) {
  const last = opts?.throughCurrentMonth ? currentMonth(today) : lastCompleteMonth(today);
  if (last === null) return [];
  return records.filter((r) => r.mk <= last);
}

/** The calendar month `today` (a Date) falls in, 'YYYY-MM'. */
export function currentMonth(today) {
  const d = today instanceof Date ? today : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Work-order detail rows (needs the site columns, MER-855). Each row is
 * classified the same way the counts are, so the tables and the tiles can
 * never disagree about which work orders are in scope.
 */
export function detailRecords(rows, cfg) {
  const out = [];
  if (!cfg || !Array.isArray(rows)) return out;
  for (const r of rows) {
    const lobCfg = cfg[r.lob];
    if (!lobCfg) continue;
    const role = roleFor(r.work_type, lobCfg);
    if (!role) continue;
    if (isExcludedRoute(r.route_id, lobCfg.exclude_route_prefixes)) continue;
    const mk = monthKey(r.event_date);
    if (!mk) continue;
    out.push({
      lob: r.lob,
      role,
      work_type: String(r.work_type),
      work_order: r.event_id == null ? "" : String(r.event_id),
      date: String(r.event_date).slice(0, 10),
      mk,
      div: divisionLabel(r.division),
      divSlug: divisionSlug(r.division),
      size: sizeLabel(r.volume_collected_cy),
      site_id: r.site_id == null ? "" : String(r.site_id),
      site_name: r.site_name == null ? "" : String(r.site_name),
    });
  }
  return out.sort((a, b) =>
    a.date === b.date ? a.work_order.localeCompare(b.work_order) : a.date.localeCompare(b.date),
  );
}

/**
 * Whether the site columns can back the detail views: "missing" until the
 * columns exist (MER-855 migration), "empty" while they exist but no row in
 * scope carries a site yet (awaiting the backfill refresh), else "ready".
 */
export function siteColumnState(columnsPresent, detail) {
  if (!columnsPresent) return "missing";
  if (!Array.isArray(detail) || !detail.some((r) => r.site_id || r.site_name)) return "empty";
  return "ready";
}

/** CSV text for the work order log (one row per work order). */
export function workOrderLogCsv(detail) {
  const header = [
    "Work Order",
    "Scheduled Date",
    "Division",
    "Line",
    "Type",
    "Size",
    "Site Id",
    "Site Name",
  ];
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(",")];
  for (const r of detail) {
    lines.push(
      [
        r.work_order,
        r.date,
        r.div,
        LOB_SHORT[r.lob] ?? r.lob,
        r.work_type,
        r.size,
        r.site_id,
        r.site_name,
      ]
        .map(esc)
        .join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

/*
 * REPORT PORT ADAPTER (src/reports/containers). The client's Container
 * Inventory page runs every view off one array of work orders shaped
 *   {wo, site, addr, div, type, size, date: "MM/DD/YYYY", mk: "YYYY-MM", lob}
 * with lob "RO" | "FEL" and type "Delivery" | "Removal" (roll off),
 * "FL Delivery" | "FL Removal" (front load), or the other work type's name.
 * These functions turn our records into that shape so the page's own filters
 * and counts run unchanged. They decide nothing new: the role of a work type,
 * the route exclusion and the size come from buildRecords / detailRecords.
 */

/** Our line of business to the page's lob code. */
export const PAGE_LOB = {
  roll_off: "RO",
  front_load: "FEL",
  hook_lift: "HL",
  tractor_trailer: "TT",
};

/**
 * The page's type label for one line and role; an "other" keeps its name.
 * Hook lift and tractor trailer carry their own prefix ("HL Delivery"), so no
 * count the page makes on "Delivery" or "FL Delivery" ever takes them in
 * (AJ 2026-10-21, #1057).
 */
export function pageType(lob, role, workType) {
  const ro = lob === "roll_off";
  const own = lob === "hook_lift" || lob === "tractor_trailer" ? `${LOB_SHORT[lob]} ` : null;
  if (role === "delivery") return ro ? "Delivery" : own ? `${own}Delivery` : "FL Delivery";
  if (role === "removal") return ro ? "Removal" : own ? `${own}Removal` : "FL Removal";
  return String(workType ?? "");
}

/**
 * The page's lines beyond the client tool's own two (roll off and front load):
 * hook lift and tractor trailer, each its own line with its own deliveries,
 * removals and Net Yard, never pooled with roll off (AJ 2026-10-21, #1057).
 * The port draws one only when the customer's config names its work types, so
 * a customer without them sees the client's page unchanged.
 */
export const PAGE_EXTRA_LINES = Object.freeze(
  ["hook_lift", "tractor_trailer"].map((key) =>
    Object.freeze({
      key,
      lob: PAGE_LOB[key],
      title: key === "hook_lift" ? "Hook Lift" : "Tractor Trailer",
      delType: pageType(key, "delivery", ""),
      remType: pageType(key, "removal", ""),
    }),
  ),
);

/** The extra lines this customer's normalised config carries, in display order. */
export function pageExtraLines(cfg) {
  if (!cfg || typeof cfg !== "object") return [];
  return PAGE_EXTRA_LINES.filter((l) => Boolean(cfg[l.key]));
}

/** "2026-08-05" to the page's "08/05/2026"; empty when unparseable. */
export function pageDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof iso === "string" ? iso : "");
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
}

/** The page's "08/05/2026" back to "2026-08-05"; empty when unparseable. */
export function pageDateIso(date) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(typeof date === "string" ? date.trim() : "");
  return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : "";
}

/**
 * Grouped records (buildRecords) to page rows: one row per work order, as the
 * page's CSV has, so its `.filter(...).length` counts equal our sums. A
 * grouped count carries no work order number or site, so those are empty.
 */
export function pageRows(records) {
  const out = [];
  if (!Array.isArray(records)) return out;
  for (const r of records) {
    const row = {
      wo: "",
      site: "",
      addr: "",
      div: r.div,
      type: pageType(r.lob, r.role, r.work_type),
      size: r.size,
      date: pageDate(r.date),
      mk: r.mk,
      lob: PAGE_LOB[r.lob] ?? String(r.lob),
    };
    const n = Math.max(0, Math.floor(Number(r.n) || 0));
    for (let i = 0; i < n; i++) out.push(i === 0 ? row : { ...row });
  }
  return out;
}

/**
 * Work-order detail (detailRecords) to page rows. The work order and the
 * site name are ours; the source carries no street address column, so addr
 * is empty and the page prints its placeholder.
 */
export function pageDetailRows(detail) {
  if (!Array.isArray(detail)) return [];
  return detail.map((r) => ({
    wo: r.work_order,
    site: r.site_name,
    addr: "",
    div: r.div,
    type: pageType(r.lob, r.role, r.work_type),
    size: r.size,
    date: pageDate(r.date),
    mk: r.mk,
    lob: PAGE_LOB[r.lob] ?? String(r.lob),
  }));
}

/**
 * Reconciliation "Nav Net" for one division, line and size: removals minus
 * deliveries. The page's card is captioned "Physical count + post-count
 * Navusoft activity", i.e. the yard as counted plus what moved AFTER the
 * count; that stated intent is what this ports: with a count date, only
 * activity scheduled strictly after it counts. (The page's own code counts
 * every row loaded, whatever the count date; see the PR.) With no count
 * date (no count recorded) every row counts, which is what the page shows.
 *
 * @param {Array} rows page rows
 * @param {{lob:string, div:string, size:string, delType:string,
 *   remType:string, countDate?:string|null}} q countDate is YYYY-MM-DD
 */
export function reconNavNet(rows, q) {
  let deliveries = 0;
  let removals = 0;
  const after = typeof q.countDate === "string" && q.countDate ? q.countDate : null;
  for (const r of rows ?? []) {
    if (r.lob !== q.lob || r.div !== q.div || r.size !== q.size) continue;
    if (after !== null && !(pageDateIso(r.date) > after)) continue;
    if (r.type === q.delType) deliveries++;
    else if (r.type === q.remType) removals++;
  }
  return { deliveries, removals, net: netYard(deliveries, removals) };
}

/* ------------------------------------------------------------------ */
/* Physical counts (0090 container_physical_count)                     */
/* ------------------------------------------------------------------ */

/**
 * The lines and sizes a physical count is taken for, as the page's Counts
 * form lists them (the port's RO_SIZES and FL_SIZES; a test pins the two
 * together). Settings > Inputs draws the latest counts from this.
 */
export const PHYSICAL_COUNT_LINES = Object.freeze([
  Object.freeze({
    lob: "RO",
    title: "Roll off",
    sizes: Object.freeze(["20 YD", "30 YD", "40 YD"]),
  }),
  Object.freeze({
    lob: "FEL",
    title: "Front load",
    sizes: Object.freeze(["8 YD", "6 YD", "4 YD", "2 YD"]),
  }),
]);

/** The key a count is stored and superseded under: line, division, size. */
export function countKey(lob, div, size) {
  return `${lob}|${div}|${size}`;
}

function newerCount(a, b) {
  const ra = String(a.recorded_at ?? "");
  const rb = String(b.recorded_at ?? "");
  if (ra !== rb) return ra > rb;
  return String(a.counted_on ?? "") > String(b.counted_on ?? "");
}

/**
 * The count in force per (line, division, size): the newest recorded row
 * wins. The table is append-only, so a recount supersedes and never
 * rewrites.
 */
export function latestCounts(rows) {
  const out = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r) continue;
    const k = countKey(r.line_of_business, r.division, r.container_size);
    const cur = out.get(k);
    if (!cur || newerCount(r, cur)) out.set(k, r);
  }
  return out;
}

/**
 * The page's Count History: one entry per save (the page's physCounts
 * entries), newest first. A save is the rows one Save button wrote, which
 * share line, recorded_at (one insert, one transaction time) and writer.
 * counts[division] = { date, sizes: { [size]: count } }.
 */
export function countHistory(rows) {
  const byEntry = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r) continue;
    const k = `${r.line_of_business}|${r.recorded_at}|${r.recorded_by ?? ""}`;
    let e = byEntry.get(k);
    if (!e) {
      e = { lob: r.line_of_business, savedAt: r.recorded_at, counts: {} };
      byEntry.set(k, e);
    }
    const d = (e.counts[r.division] ??= { date: r.counted_on ?? "", sizes: {} });
    d.sizes[r.container_size] = r.count;
  }
  return [...byEntry.values()].sort((a, b) =>
    String(b.savedAt ?? "").localeCompare(String(a.savedAt ?? "")),
  );
}

/**
 * One Reconciliation card (the page's buildCard): per size, the physical
 * count in force, Nav Net after that count's date (reconNavNet), and Actual
 * On Yard = physical + Nav Net. A size with no count of its own counts 0 and
 * takes the division's count date. `date` is the division's newest count
 * date, or null when nothing was counted for the card.
 */
export function reconCard(rows, latest, q) {
  const got = q.sizes.map((sz) => latest?.get(countKey(q.lob, q.div, sz)) ?? null);
  const dates = got.filter(Boolean).map((r) => String(r.counted_on ?? ""));
  const date = dates.length ? dates.sort()[dates.length - 1] : null;
  const out = q.sizes.map((size, i) => {
    const row = got[i];
    const phys = row ? Number(row.count) || 0 : 0;
    const countDate = row?.counted_on ?? date;
    const nav = reconNavNet(rows, {
      lob: q.lob,
      div: q.div,
      size,
      delType: q.delType,
      remType: q.remType,
      countDate,
    }).net;
    return { size, phys, nav, actual: phys + nav };
  });
  return {
    date,
    rows: out,
    physTotal: out.reduce((s, r) => s + r.phys, 0),
    actualTotal: out.reduce((s, r) => s + r.actual, 0),
  };
}
